import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { StatusBadge } from "@/components/StatusBadge";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  RadialBarChart, RadialBar, PolarAngleAxis,
} from "recharts";
import { TrendingUp, Users, CheckCircle2, Banknote, Wallet, Clock, Trophy } from "lucide-react";

function Kpi({ label, value, sub, icon: Icon, accent }) {
  return (
    <div data-testid={`kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}
      className="p-5 rounded-xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-all relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-1 ${accent}`} />
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</div>
          <div className="text-2xl font-extrabold text-slate-900 font-heading mt-2">{value}</div>
          {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
        </div>
        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600"><Icon size={20} /></div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="text-slate-400 text-sm">Loading dashboard…</div>;

  const funnel = stats.funnel.filter((f) => f.stage !== "Rejected");
  const gaugeData = [{ name: "approval", value: stats.approval_pct, fill: "#10B981" }];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Real-time overview of your loan sourcing operations</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <Kpi label="Total Leads" value={stats.total_leads} sub="All time" icon={Users} accent="bg-gradient-to-r from-slate-500 to-slate-700" />
        <Kpi label="Total Login" value={stats.total_login} sub="Logged in with lenders" icon={TrendingUp} accent="bg-gradient-to-r from-blue-500 to-indigo-600" />
        <Kpi label="Approval %" value={`${stats.approval_pct}%`} sub="Approved + Disbursed" icon={CheckCircle2} accent="bg-gradient-to-r from-teal-500 to-emerald-600" />
        <Kpi label="Disbursement %" value={`${stats.disbursement_pct}%`} sub={`${stats.disbursed_count} disbursed`} icon={Banknote} accent="bg-gradient-to-r from-emerald-500 to-emerald-700" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
        <Kpi label="Monthly Payout" value={formatINR(stats.monthly_payout)} sub="This month (net)" icon={Wallet} accent="bg-gradient-to-r from-amber-500 to-orange-600" />
        <Kpi label="Pending Payout" value={formatINR(stats.pending_payout)} sub="Awaiting settlement" icon={Clock} accent="bg-gradient-to-r from-rose-500 to-rose-700" />
        <Kpi label="Total Disbursed" value={formatINR(stats.total_disbursed_amount)} sub="Loan value" icon={Banknote} accent="bg-gradient-to-r from-emerald-500 to-blue-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 p-5 rounded-xl bg-white border border-slate-200 shadow-sm">
          <h3 className="text-base font-semibold text-slate-800 font-heading mb-4">Loan Pipeline Funnel</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={funnel} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <XAxis dataKey="stage" tick={{ fontSize: 11, fill: "#64748b" }} interval={0} angle={-15} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
              <Tooltip cursor={{ fill: "#f1f5f9" }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {funnel.map((_, i) => <Cell key={i} fill={["#94a3b8", "#f59e0b", "#3b82f6", "#6366f1", "#14b8a6", "#10b981"][i]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="p-5 rounded-xl bg-white border border-slate-200 shadow-sm flex flex-col items-center">
          <h3 className="text-base font-semibold text-slate-800 font-heading mb-2 self-start">Approval Rate</h3>
          <ResponsiveContainer width="100%" height={220}>
            <RadialBarChart innerRadius="70%" outerRadius="100%" data={gaugeData} startAngle={90} endAngle={-270}>
              <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
              <RadialBar dataKey="value" cornerRadius={20} background={{ fill: "#f1f5f9" }} />
            </RadialBarChart>
          </ResponsiveContainer>
          <div className="-mt-32 text-center">
            <div className="text-4xl font-extrabold text-slate-900 font-heading">{stats.approval_pct}%</div>
            <div className="text-xs text-slate-500 mt-1">of leads approved</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 p-5 rounded-xl bg-white border border-slate-200 shadow-sm">
          <h3 className="text-base font-semibold text-slate-800 font-heading mb-4">Product-wise Sales</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats.product} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <XAxis dataKey="loan_type" tick={{ fontSize: 10, fill: "#64748b" }} interval={0} angle={-15} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
              <Tooltip cursor={{ fill: "#f1f5f9" }} />
              <Bar dataKey="count" name="Total Leads" fill="#2563eb" radius={[6, 6, 0, 0]} />
              <Bar dataKey="disbursed" name="Disbursed" fill="#10b981" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="p-5 rounded-xl bg-white border border-slate-200 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Trophy size={18} className="text-amber-500" />
            <h3 className="text-base font-semibold text-slate-800 font-heading">Top Performing DSAs</h3>
          </div>
          <div className="space-y-3">
            {stats.top_dsas.length === 0 && <div className="text-sm text-slate-400">No disbursements yet</div>}
            {stats.top_dsas.map((d, i) => (
              <div key={d.partner_id} data-testid={`top-dsa-${i}`} className="flex items-center gap-3">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{d.partner_name}</div>
                  <div className="text-xs text-slate-400">{d.disbursed} disbursed · {formatINR(d.payout)} payout</div>
                </div>
                <div className="text-sm font-semibold text-emerald-600">{formatINR(d.disbursed_amount)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
